/**
 * OpenID discovery document fetch + short-lived in-memory cache.
 */

export interface OpenIdDiscoveryConfiguration {
    "issuer":                string;
    "authorizationEndpoint": string;
    "tokenEndpoint":         string;
    "jwksUri":               string;
}

interface OpenIdDiscoveryDocument {
    "issuer":                 string;
    "authorization_endpoint": string;
    "token_endpoint":         string;
    "jwks_uri":               string;
}

interface CacheEntry {
    "configuration": OpenIdDiscoveryConfiguration;
    "fetchedAtMs":   number;
}

const CACHE_TTL_MS = 5 * 60 * 1000;

let cache: CacheEntry | null = null;

/**
 * Returns OpenID Provider metadata for the configured discovery URL.
 *
 * @param configurationLink - Absolute `.well-known/openid-configuration` URL.
 * @param forceRefresh - When true, bypasses the in-memory cache.
 * @returns Normalized authorization / token / JWKS endpoints.
 */
export async function getOpenIdConfiguration(
    configurationLink: string,
    forceRefresh = false,
): Promise<OpenIdDiscoveryConfiguration> {
    const link = configurationLink.trim();
    if ( link === "" ) {
        throw new Error( "SwissPass OpenID configuration link is not set" );
    }

    const now = Date.now();
    if (
        !forceRefresh &&
        cache != null &&
        cache.configuration != null &&
        now - cache.fetchedAtMs < CACHE_TTL_MS
    ) {
        return cache.configuration;
    }

    const response = await fetch( link, { "headers": { "Accept": "application/json" } } );
    if ( !response.ok ) {
        throw new Error( `OpenID discovery failed with status ${ response.status }` );
    }

    const document = await response.json() as OpenIdDiscoveryDocument;
    if (
        typeof document.issuer !== "string" ||
        typeof document.authorization_endpoint !== "string" ||
        typeof document.token_endpoint !== "string" ||
        typeof document.jwks_uri !== "string"
    ) {
        throw new Error( "OpenID discovery document is incomplete" );
    }

    const configuration: OpenIdDiscoveryConfiguration = {
        "issuer":                document.issuer,
        "authorizationEndpoint": document.authorization_endpoint,
        "tokenEndpoint":         document.token_endpoint,
        "jwksUri":               document.jwks_uri,
    };
    cache = {
        configuration,
        "fetchedAtMs": now,
    };

    return configuration;
}
